import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useProfiles } from "./ProfileContext";
import { PROFILE_TYPE_LABELS, type Profile } from "../lib/profiles";
import { Icon } from "../app/Icon";

/** 2-letter initials for a profile avatar tile. */
export function initials(s?: string | null): string {
  if (!s) return "?";
  const parts = s.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return s.slice(0, 2).toUpperCase();
}

function Avatar({ profile, size = "sm" }: { profile: Profile; size?: "sm" | "md" }) {
  const cls = size === "md" ? "h-8 w-8 text-xs" : "h-6 w-6 text-[11px]";
  const base = `grid shrink-0 place-items-center overflow-hidden rounded-md bg-accent/15 font-display font-black text-accent-strong ${cls}`;
  if (profile.image_url) return <span className={base}><img src={profile.image_url} alt="" className="h-full w-full object-cover" /></span>;
  return <span className={base}>{initials(profile.display_name ?? PROFILE_TYPE_LABELS[profile.type])}</span>;
}

/** Top-bar active-profile menu: switch, create, or manage profiles. */
export function ProfileMenu() {
  const { profiles, active, setActive } = useProfiles();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onDoc(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  if (profiles.length === 0) {
    return (
      <Link to="/profiles/new" className="rounded-lg border border-line bg-surface px-3 py-1.5 text-sm font-bold text-accent-strong">
        + Create a profile
      </Link>
    );
  }

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Active profile"
        className="flex items-center gap-2 rounded-lg border border-line bg-surface px-2.5 py-1.5 text-sm font-bold text-ink hover:border-accent"
      >
        <span className="h-2 w-2 rounded-full bg-verified" aria-hidden />
        {active && <Avatar profile={active} />}
        <span className="hidden max-w-[9rem] truncate sm:inline">{active?.display_name ?? (active ? PROFILE_TYPE_LABELS[active.type] : "—")}</span>
        <Icon name="chevron" className={`h-4 w-4 text-muted transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div role="menu" className="absolute right-0 z-40 mt-2 w-72 rounded-card border border-line bg-surface p-2 shadow-xl">
          <p className="px-2 pb-1 pt-1.5 text-[10px] font-bold uppercase tracking-widest text-muted">Active profile</p>
          {profiles.map((p) => (
            <button
              key={p.id}
              type="button"
              role="menuitem"
              onClick={() => {
                setActive(p.id);
                setOpen(false);
              }}
              className="flex w-full items-center gap-2.5 rounded-lg px-2 py-2 text-left transition-colors hover:bg-surface-2"
            >
              <Avatar profile={p} size="md" />
              <span className="min-w-0 flex-1 truncate text-sm">
                <span className="font-bold">{p.display_name ?? PROFILE_TYPE_LABELS[p.type]}</span>
                {p.display_name && <span className="text-muted"> · {PROFILE_TYPE_LABELS[p.type]}</span>}
              </span>
              {active?.id === p.id && <span className="font-mono text-sm text-verified">✓</span>}
            </button>
          ))}
          <div className="my-1 border-t border-line" />
          <Link to="/profiles/new" onClick={() => setOpen(false)} className="flex items-center gap-2.5 rounded-lg px-2 py-2 text-sm font-semibold text-ink transition-colors hover:bg-surface-2">
            <Icon name="add" className="h-4 w-4" /> Create new profile
          </Link>
          <Link to="/profiles" onClick={() => setOpen(false)} className="flex items-center gap-2.5 rounded-lg px-2 py-2 text-sm font-semibold text-ink transition-colors hover:bg-surface-2">
            <Icon name="settings" className="h-4 w-4" /> Manage profiles
          </Link>
        </div>
      )}
    </div>
  );
}
